import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';

import { IBetslipResultSummaryViewModel } from '../models/betslip-result-summary.model';

interface IResultMessages {
    numberOfBets: string;
    possibleWinningsNet: string;
    possibleWinnings: string;
    totalStake: string;
    stake: string;
    teaserStake?: string;
    totalOdds: string;
    possibleWinningsGross: string;
    BoostedWinnings: string;
    freebetText: string;
}

export function provideSummaryMessages(
    summary: Nullable<IBetslipResultSummaryViewModel>,
    isEachWay: boolean,
    messages: IResultMessages,
    isTeaserBet?: boolean,
): Nullable<IBetslipResultSummaryViewModel> {
    if (summary != null) {
        if (summary.numberOfBets) {
            summary.numberOfBets.title = messages.numberOfBets;
        }
        if (summary.possibleWinnings) {
            summary.possibleWinnings.title =
                ((summary.taxation?.taxationValue?.amount?.greaterThan(0) || summary.taxation?.taxationValue?.isPlaceholderTax) ?? false)
                    ? messages.possibleWinningsNet
                    : messages.possibleWinnings;
        }

        if (summary.stake) {
            if (summary.numberOfBets.value === 1 && summary.usedRewardTokens.some((x) => x.tokenType === RewardTokenType.FreeBet)) {
                summary.stake.title = messages.freebetText;
            } else {
                summary.stake.title = isTeaserBet
                    ? messages.teaserStake!
                    : (summary.numberOfBets?.value ?? 0) > 1 || isEachWay
                      ? messages.totalStake
                      : messages.stake;
            }
        }
        if (summary.odds) {
            summary.odds.title = messages.totalOdds;
        }
        if (summary.taxation) {
            summary.taxation.possibleWinningsGrossTitle = messages.possibleWinningsGross;
        }
        if (summary.possibleWinnings.boostedValue) {
            summary.possibleWinnings.title = messages.BoostedWinnings;
        }
    }

    return summary;
}

export function createEnhancedSummary(
    oldSummary: IBetslipResultSummaryViewModel,
    isEachWay: boolean,
    messages: IResultMessages,
    isTeaserBet?: boolean,
): Nullable<IBetslipResultSummaryViewModel> {
    const numberOfBets = oldSummary.numberOfBets && {
        value: oldSummary.numberOfBets.value,
        title: messages.numberOfBets,
    };

    const possibleWinnings = oldSummary.possibleWinnings && {
        value: oldSummary.possibleWinnings.value,
        boostedValue: oldSummary.possibleWinnings.boostedValue,
        title: getPossibleWinningsTitle(oldSummary, messages),
    };

    const odds = oldSummary.odds && {
        value: oldSummary.odds.value,
        title: messages.totalOdds,
        boosted: oldSummary.odds.boosted,
    };

    const taxation = oldSummary.taxation && {
        taxationValue: oldSummary.taxation.taxationValue,
        possibleWinningsGross: oldSummary.taxation.possibleWinningsGross,
        possibleWinningsGrossTitle: messages.possibleWinningsGross,
    };

    const stake = oldSummary.stake && {
        value: oldSummary.stake.value,
        title: getStakeTitle(oldSummary, messages, isEachWay, isTeaserBet),
    };

    const freebetStake = oldSummary.freebetStake;

    return {
        currency: oldSummary.currency,
        isPotentailReturn: oldSummary.isPotentailReturn,
        numberOfBets,
        possibleWinnings,
        odds,
        taxation,
        stake,
        freebetStake,
        usedRewardTokens: [...oldSummary.usedRewardTokens],
        tokenPayoutSummaries: [...oldSummary.tokenPayoutSummaries],
    };
}

function getPossibleWinningsTitle(oldSummary: IBetslipResultSummaryViewModel, messages: IResultMessages): string {
    if (oldSummary.possibleWinnings.boostedValue) {
        return messages.BoostedWinnings;
    }

    return ((oldSummary.taxation?.taxationValue?.amount?.greaterThan(0) || oldSummary.taxation?.taxationValue?.isPlaceholderTax) ?? false)
        ? messages.possibleWinningsNet
        : messages.possibleWinnings;
}

function getStakeTitle(
    oldSummary: IBetslipResultSummaryViewModel,
    messages: IResultMessages,
    isEachWay: boolean,
    isTeaserBet: boolean | undefined,
): string {
    if (oldSummary.numberOfBets.value === 1 && oldSummary.usedRewardTokens.some((x) => x.tokenType === RewardTokenType.FreeBet)) {
        return messages.freebetText;
    }

    return isTeaserBet ? messages.teaserStake! : (oldSummary.numberOfBets?.value ?? 0) > 1 || isEachWay ? messages.totalStake : messages.stake;
}
